// Seasonal top strip. Oct 19 2026 → Winter Beauty Packages: one tap books the
// Non-Surgical Facelift Plus ($1,449) at the client's last-used clinic.
// Window Oct 15 → Mar 31. Dismissible 24h. Hidden on staff/admin/kiosk routes.
// Height exposed via --season-banner-h so TopBar + Navbar shift down.

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Snowflake, Sparkles } from "lucide-react";

export const AUTUMN = {
  blackCherry:   "#5A1A1F",
  mushroomTaupe: "#8E7D6B",
  chocolateGlaze:"#3E2415",
  cinnamonSpice: "#B6572E",
  burgundy:      "#5F1A2A",
  oliveGreen:    "#6B7558",
  cream:         "#F4EDE1",
};

const WINTER = { navy: "#0F2A44", ice: "#E8F1F8", frost: "#C7DDF0", gold: "#C9A86C", ink: "#10233A" };
const HYDRA = { teal: "#0B3B3C", mid: "#145E5A", mint: "#BFEDE4", glow: "#5EEAD4", ink: "#062626" };

const FACELIFT_VARIATION = { 3: "QK27ZL47NTRTYRMY7OJIWS45", 2: "TT6JCAEZJXJ4MKV244LNJUGH", 1: "CBFBPNPY23XRYMCPSC2SUHLZ" };
const FACELIFT_NAME = "Non-Surgical Facelift Plus with Redensity 1 Skin Booster";
// Oct 28 2026 · Karine — $149 HydraFacial promo (Tanya · Ottawa only) rotates in the strip
const HYDRA_PROMO_NAME = "PROMO · HydraFacial + Facial Steam + Lymphatic Drain — $149 +tx (reg $295)";
const HYDRA_BOOK_HREF = `/book?${new URLSearchParams({ service: HYDRA_PROMO_NAME, provider: "tanya", location: "3", src: "promo-bar-hydrafacial-149" }).toString()}`;
const ROTATE_MS = 7000;

function isWinterSeason(now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth();
  if (m >= 9) return now >= new Date(y, 9, 15);           // Oct 15 → Dec 31
  return now <= new Date(y, 2, 31, 23, 59, 59);          // Jan 1 → Mar 31
}

const DISMISS_KEY = "kl_winter_banner_dismissed_until";

const SUPPRESSED_PREFIXES = [
  "/staff", "/admin", "/md", "/np/join", "/consultation/", "/consent",
  "/welcome", "/checkin", "/signage", "/lobby", "/kiosk",
];

export default function FallPromoBanner() {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [slide, setSlide] = useState(0); // 0 = Winter Beauty Packages · 1 = $149 HydraFacial promo
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setSlide((s) => (s + 1) % 2), ROTATE_MS);
    return () => clearInterval(id);
  }, [paused]);

  useEffect(() => {
    try {
      const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (until && Date.now() < until) setDismissed(true);
    } catch {}
  }, []);

  const suppressed = useMemo(
    () => SUPPRESSED_PREFIXES.some((p) => location.pathname.startsWith(p)),
    [location.pathname]
  );

  const visible = isWinterSeason() && !suppressed && !dismissed;

  useEffect(() => {
    const root = document.documentElement;
    if (visible) root.style.setProperty("--season-banner-h", "36px");
    else root.style.removeProperty("--season-banner-h");
    return () => root.style.removeProperty("--season-banner-h");
  }, [visible]);

  if (!visible) return null;

  let loc = "3";
  try { loc = localStorage.getItem("kl_last_location_id") || "3"; } catch {}
  if (!FACELIFT_VARIATION[loc]) loc = "3";
  const bookHref = `/book?service=${encodeURIComponent(FACELIFT_NAME)}&variation=${FACELIFT_VARIATION[loc]}&location=${loc}&src=winter-banner`;

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000)); } catch {}
    setDismissed(true);
  };

  const isHydra = slide === 1;
  const href = isHydra ? HYDRA_BOOK_HREF : bookHref;
  const T = isHydra ? HYDRA : WINTER;

  return (
    <div
      className="fixed top-0 left-0 right-0 w-full z-[200]"
      style={{
        background: isHydra
          ? `linear-gradient(90deg, ${HYDRA.teal} 0%, ${HYDRA.mid} 50%, ${HYDRA.teal} 100%)`
          : `linear-gradient(90deg, ${WINTER.navy} 0%, #1B3F63 50%, ${WINTER.navy} 100%)`,
        borderBottom: `1px solid ${isHydra ? HYDRA.glow : WINTER.gold}55`,
        transition: "background 600ms ease",
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="winter-promo-banner"
      data-slide={isHydra ? "hydrafacial-149" : "winter-facelift"}
    >
      <Link
        key={slide}
        to={href}
        className="flex items-center justify-center gap-2 sm:gap-3 pl-3 pr-10 py-2 sm:py-3 text-center text-[12px] sm:text-[13px] font-medium tracking-wide animate-in fade-in duration-500"
        style={{ color: isHydra ? HYDRA.mint : WINTER.ice }}
        data-testid={isHydra ? "hydrafacial-promo-banner-link" : "winter-promo-banner-link"}
      >
        {isHydra ? (
          <>
            <Sparkles size={16} className="flex-shrink-0" style={{ color: HYDRA.glow }} />
            <span className="font-serif italic hidden md:inline" style={{ color: HYDRA.glow }}>
              New with Tanya · Ottawa
            </span>
            <span className="hidden md:inline" style={{ opacity: 0.4 }}>·</span>
            <span className="sm:hidden whitespace-nowrap">
              HydraFacial + Steam + Lymphatic
              <span className="mx-1" style={{ opacity: 0.6 }}>·</span>
              <strong style={{ color: HYDRA.glow }}>$149</strong>
            </span>
            <span className="hidden sm:inline">
              <strong style={{ color: "#fff" }}>HydraFacial + Facial Steam + Lymphatic Drain</strong>
              <span className="mx-1.5" style={{ opacity: 0.5 }}>·</span>
              <span className="line-through" style={{ opacity: 0.6 }}>$295</span>
              <span className="mx-1.5 font-semibold" style={{ color: HYDRA.glow }}>$149</span>
              <span className="hidden md:inline" style={{ opacity: 0.6 }}>+ tax · add Dermaplaning +$49</span>
            </span>
          </>
        ) : (
          <>
            <Snowflake size={16} className="flex-shrink-0" style={{ color: WINTER.frost }} />
            <span className="font-serif italic hidden md:inline" style={{ color: WINTER.gold }}>
              Winter Beauty Packages
            </span>
            <span className="hidden md:inline" style={{ opacity: 0.4 }}>·</span>

            <span className="sm:hidden whitespace-nowrap">
              Non-Surgical Facelift Plus
              <span className="mx-1" style={{ opacity: 0.6 }}>·</span>
              <strong style={{ color: WINTER.gold }}>$1,449</strong>
            </span>

            <span className="hidden sm:inline">
              <strong style={{ color: "#fff" }}>Non-Surgical Facelift Plus</strong>
              <span className="mx-1.5" style={{ opacity: 0.5 }}>·</span>
              Full Face &amp; Neck Botox incl. Nefertiti Lift + 3 mL Redensity 1
              <span className="mx-1.5 font-semibold" style={{ color: WINTER.gold }}>$1,449</span>
              <span className="hidden md:inline" style={{ opacity: 0.6 }}>+ tax</span>
            </span>
          </>
        )}

        <span
          className="ml-1 sm:ml-2 inline-flex items-center gap-1 px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-[11px] font-bold uppercase tracking-wider transition-transform hover:scale-105 whitespace-nowrap"
          style={{ background: isHydra ? HYDRA.glow : WINTER.gold, color: T.ink, boxShadow: `0 2px 8px ${isHydra ? HYDRA.glow : WINTER.gold}66` }}
          data-testid={isHydra ? "hydrafacial-promo-banner-cta" : "winter-promo-banner-cta"}
        >
          Book <span className="hidden sm:inline">Now </span>→
        </span>
      </Link>
      <div className="absolute left-2 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-1.5" data-testid="promo-banner-dots">
        {[0, 1].map((i) => (
          <button
            key={i}
            type="button"
            aria-label={i === 0 ? "Show Winter Beauty Packages" : "Show $149 HydraFacial promo"}
            onClick={(e) => { e.preventDefault(); setSlide(i); }}
            className="h-1.5 rounded-full transition-all"
            style={{ width: slide === i ? 14 : 6, background: slide === i ? (isHydra ? HYDRA.glow : WINTER.gold) : "rgba(255,255,255,0.35)" }}
            data-testid={i === 0 ? "promo-banner-dot-winter" : "promo-banner-dot-hydrafacial"}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss promo banner"
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-white/10 transition"
        style={{ color: isHydra ? HYDRA.mint : WINTER.ice }}
        data-testid="winter-promo-banner-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
